import React, { useEffect, useState } from 'react';
import { Table, Form, Input, Button, Select, DatePicker, Row, Col, Modal, message } from 'antd';
import masterService from '../../../services/masterService';

const { RangePicker } = DatePicker;

const statusOptions = [
  { label: 'PENDING', value: 'PENDING' },
  { label: 'INITIATED', value: 'INITIATED' },
  { label: 'SUCCESS', value: 'SUCCESS' },
  { label: 'FAILED', value: 'FAILED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
  { label: 'REFUND_PENDING', value: 'REFUND_PENDING' },
  { label: 'REFUND_INITIATED', value: 'REFUND_INITIATED' },
  { label: 'REFUND_SUCCESS', value: 'REFUND_SUCCESS' },
  { label: 'REFUND_FAILED', value: 'REFUND_FAILED' },
];
const sourceOptions = [
  { label: 'TAP_PAY', value: 'TAP_PAY' },
  { label: 'APPLE_PAY', value: 'APPLE_PAY' },
  { label: 'APPLE_IN_APP_PURCHASE', value: 'APPLE_IN_APP_PURCHASE' },
];
const triggerOptions = [
  { label: 'MANUAL', value: 'MANUAL' },
  { label: 'AUTO', value: 'AUTO' },
  { label: 'ADMIN', value: 'ADMIN' },
];

// Sort params for the API (empty when no active sort)
const sortParams = (s) =>
  s.field && s.order
    ? { sortBy: s.field, sortOrder: s.order === 'ascend' ? 'asc' : 'desc' }
    : {};

function Payment() {
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [planOptions, setPlanOptions] = useState([]);
  const [planTypeOptions, setPlanTypeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [subscriptionModal, setSubscriptionModal] = useState({ open: false, data: null });
  // Server-side sort state ({ field, order } with antd's ascend/descend)
  const [sortState, setSortState] = useState({ field: null, order: null });

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const { data: res } = await masterService.getPaymentList({
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...params.filters,
      });

      const subscriptionByPaymentId = new Map(
        (res.subscriptionMatchedPaymentIds || []).map((item) => [item.fk_payment_id, item])
      );

      const mappedData = (res.data || []).map((item) => ({
        ...item,
        user_email: item.u_user?.user_email || '',
        user_country_code: item.u_user?.user_country_code || '',
        user_mobile: item.u_user?.user_mobile || '',
        plan_name: item.subscription_plan?.title || '',
        plan_type_name: item.m_plan_type?.name || '',
        subscription_details: subscriptionByPaymentId.get(item.id) || null,
        subscription: subscriptionByPaymentId.has(item.id) ? 'Yes' : 'No',
      }));
      setData(mappedData);
      setPlanOptions((res.subscription_plan || []).map((item) => ({
        label: item.title,
        value: item.title,
      })));
      setPlanTypeOptions((res.m_plan_type || []).map((item) => ({
        label: item.name,
        value: item.name,
      })));
      setCurrencyOptions((res.m_currency || []).map((item) => ({
        label: item.curr_code,
        value: item.curr_code,
        curr_id: item.curr_id,
      })));
      setPagination({
        current: res.page,
        pageSize: res.pageSize,
        total: res.total,
      });
    } catch (err) {
      console.error('Error fetching payments', err);
      message.error('Failed to fetch payments');
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const buildFilters = (values = {}) => {
    const filters = { ...values };

    ['email', 'mobile_code', 'mobile'].forEach((key) => {
      if (filters[key] !== undefined) {
        const val = String(filters[key]).trim();
        if (val) filters[key] = val;
        else delete filters[key];
      }
    });

    if (filters.plan_name !== undefined) {
      const planName = String(filters.plan_name).trim();
      if (planName) filters.plan_name = planName;
      else delete filters.plan_name;
    }

    if (filters.plan_type_name !== undefined) {
      const planTypeName = String(filters.plan_type_name).trim();
      if (planTypeName) filters.plan_type_name = planTypeName;
      else delete filters.plan_type_name;
    }

    if (filters.currency !== undefined) {
      const currencyCode = String(filters.currency).trim();
      if (currencyCode) filters.currency = currencyCode;
      else delete filters.currency;
    }

    if (filters.dateRange) {
      filters.startDate = filters.dateRange[0]?.format('YYYY-MM-DD');
      filters.endDate = filters.dateRange[1]?.format('YYYY-MM-DD');
      delete filters.dateRange;
    }

    return filters;
  };

  const onFinish = values => {
    fetchData({ current: 1, filters: { ...buildFilters(values), ...sortParams(sortState) } });
  };

  const handleTableChange = (pag, tableFilters, sorter) => {
    // 3-state column sort (asc → desc → normal), applied server-side
    const nextSort = sorter?.order
      ? { field: sorter.field, order: sorter.order }
      : { field: null, order: null };
    const sortChanged =
      nextSort.field !== sortState.field || nextSort.order !== sortState.order;
    setSortState(nextSort);
    fetchData({
      current: sortChanged ? 1 : pag.current,
      pageSize: pag.pageSize,
      filters: { ...buildFilters(form.getFieldsValue()), ...sortParams(nextSort) },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    { title: 'User ID', dataIndex: 'fk_user_id', key: 'fk_user_id', sorter: true },
    { title: 'Email', dataIndex: 'user_email', key: 'user_email', render: (v) => v || '-' },
    { title: 'Mobile Code', dataIndex: 'user_country_code', key: 'user_country_code', render: (v) => v || '-' },
    { title: 'Mobile', dataIndex: 'user_mobile', key: 'user_mobile', render: (v) => v || '-' },
    { title: 'Plan Name', dataIndex: 'plan_name', key: 'plan_name' },
    { title: 'Plan Type Name', dataIndex: 'plan_type_name', key: 'plan_type_name' },
    { title: 'Status', dataIndex: 'payment_status', key: 'payment_status', sorter: true },
    {
      title: 'Subscription',
      dataIndex: 'subscription',
      key: 'subscription',
      render: (_, record) =>
        record.subscription_details ? (
          <Button size="small" onClick={() => setSubscriptionModal({ open: true, data: record.subscription_details })}>
            Info
          </Button>
        ) : (
          'No'
        ),
    },
    { title: 'Currency', dataIndex: 'currency', key: 'currency', render: (v) => v || '-', sorter: true },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      render: (v) => (v === null || v === undefined || v === '' ? '-' : Number(v).toFixed(2)),
    },
    { title: 'Source', dataIndex: 'payment_source', key: 'payment_source', render: (v) => v || '-', sorter: true },
    { title: 'Trigger', dataIndex: 'payment_trigger', key: 'payment_trigger', render: (v) => v || '-', sorter: true },
    { title: 'Created At', dataIndex: 'created_at', key: 'created_at', sorter: true, render: v => v && new Date(v).toLocaleString() },
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ marginBottom: 16, background: '#fff', padding: 16, borderRadius: 8 }}
      >
        <Row gutter={16}>
          <Col span={4}><Form.Item name="email" label="Email"><Input allowClear /></Form.Item></Col>
          <Col span={4}><Form.Item name="mobile_code" label="Mobile Code"><Input allowClear /></Form.Item></Col>
          <Col span={4}><Form.Item name="mobile" label="Mobile"><Input allowClear /></Form.Item></Col>
          <Col span={4}><Form.Item name="plan_name" label="Plan Name"><Select allowClear options={planOptions} /></Form.Item></Col>
          <Col span={4}><Form.Item name="plan_type_name" label="Plan Type Name"><Select allowClear options={planTypeOptions} /></Form.Item></Col>
          <Col span={4}><Form.Item name="status" label="Status"><Select allowClear options={statusOptions} /></Form.Item></Col>
          <Col span={4}><Form.Item name="paymentSource" label="Source"><Select allowClear options={sourceOptions} /></Form.Item></Col>
          <Col span={4}><Form.Item name="paymentTrigger" label="Trigger"><Select allowClear options={triggerOptions} /></Form.Item></Col>
          <Col span={4}><Form.Item name="currency" label="Currency"><Select allowClear options={currencyOptions} /></Form.Item></Col>
          <Col span={4}><Form.Item name="minAmount" label="Min Amount"><Input type="number" /></Form.Item></Col>
          <Col span={4}><Form.Item name="maxAmount" label="Max Amount"><Input type="number" /></Form.Item></Col>
          <Col span={6}><Form.Item name="dateRange" label="Created Date"><RangePicker /></Form.Item></Col>
          <Col span={4}>
            <Form.Item label=" " colon={false}>
              <Button type="primary" htmlType="submit">Filter</Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        rowKey="id"
        onChange={handleTableChange}
        bordered
        size="middle"
        scroll={{ x: 1300 }}
      />
      <Modal
        title="Subscription Details"
        open={subscriptionModal.open}
        onCancel={() => setSubscriptionModal({ open: false, data: null })}
        footer={null}
      >
        {subscriptionModal.data ? (
          <div>
            <p><strong>Subscription ID:</strong> {subscriptionModal.data.id}</p>
            <p><strong>Payment ID:</strong> {subscriptionModal.data.fk_payment_id}</p>
            <p><strong>User ID:</strong> {subscriptionModal.data.u_user?.user_id ?? '-'}</p>
            <p><strong>Username:</strong> {subscriptionModal.data.u_user?.user_firstname || '-'}</p>
            <p><strong>Status:</strong> {subscriptionModal.data.subscription_status}</p>
            <p><strong>Start:</strong> {subscriptionModal.data.subscription_start ? new Date(subscriptionModal.data.subscription_start).toLocaleString() : '-'}</p>
            <p><strong>End:</strong> {subscriptionModal.data.subscription_end ? new Date(subscriptionModal.data.subscription_end).toLocaleString() : '-'}</p>
            <p><strong>Autopay:</strong> {subscriptionModal.data.is_autopay ? 'Yes' : 'No'}</p>
            <p><strong>Active:</strong> {subscriptionModal.data.is_active ? 'Yes' : 'No'}</p>
            <p><strong>Created At:</strong> {subscriptionModal.data.created_at ? new Date(subscriptionModal.data.created_at).toLocaleString() : '-'}</p>
            <p><strong>Updated At:</strong> {subscriptionModal.data.updated_at ? new Date(subscriptionModal.data.updated_at).toLocaleString() : '-'}</p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default Payment;